import { useEffect, useMemo, useRef } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useVisionClient } from '@/app/vision-client-provider'
import { useTenantStore } from '@/app/state/tenant-store'
import { getVisionAccessToken } from '@/app/auth/token-source'
import { createValkyrieUploadClient } from '@/lib/valkyrie/client'
import { IS_DEV } from '@/lib/env'
import { getBcLotReadiness, HeimdallApiError } from '@/lib/heimdall/client'
import { VisionApiError } from '@/lib/vision/client'
import {
  orderFromCancellationResponse,
  type OrderCancellationRequest,
} from '@/lib/vision/order-cancellation-schema'
import { useBulkSelectionStore } from '@/features/bulk-warehouse-release/bulk-selection-store'
import type { BcLotReadinessResponse } from '@/lib/heimdall/schemas'
import type {
  BcSalesOrderFilter,
  Item,
  Order,
  OrderInventory,
  OrderStatus,
  ValidationRun,
  WarehouseReleaseLine,
} from '@/lib/vision/schemas'

export interface OrdersQuery {
  channel?: string[]
  channelCode?: string | null
  vendorCode?: string[]
  status?: OrderStatus[]
  bcSalesOrder?: BcSalesOrderFilter[]
  q?: string
  from?: string
  to?: string
  page: number
  size: number
  sort?: string
}

export function useOrdersQuery(params: OrdersQuery) {
  const client = useVisionClient()
  const tenantId = useTenantStore(s => s.tenantId)
  return useQuery({
    queryKey: ['orders', tenantId, params],
    queryFn: () => client.listOrders(params),
    placeholderData: prev => prev,
  })
}

export interface OrdersSummaryQuery {
  channel?: string[]
  channelCode?: string | null
  vendorCode?: string[]
  status?: OrderStatus[]
  bcSalesOrder?: BcSalesOrderFilter[]
  q?: string
  from?: string | null
  to?: string | null
}

export function useOrdersSummaryQuery(params: OrdersSummaryQuery) {
  const client = useVisionClient()
  const tenantId = useTenantStore(s => s.tenantId)
  return useQuery({
    queryKey: ['orders-summary', tenantId, params],
    queryFn: () => client.getOrdersSummary(params),
    placeholderData: prev => prev,
  })
}

export function useOrderDetailQuery(externalOrderNumber: string | undefined) {
  const client = useVisionClient()
  const tenantId = useTenantStore(s => s.tenantId)
  return useQuery({
    queryKey: ['order', tenantId, externalOrderNumber],
    queryFn: () => client.getOrder(externalOrderNumber!),
    enabled: Boolean(externalOrderNumber),
  })
}

export function useOrderAuditQuery(externalOrderNumber: string | undefined) {
  const client = useVisionClient()
  const tenantId = useTenantStore(s => s.tenantId)
  return useQuery({
    queryKey: ['order-audit', tenantId, externalOrderNumber],
    queryFn: () => client.getAudit(externalOrderNumber!),
    enabled: Boolean(externalOrderNumber),
    staleTime: 15_000,
  })
}

/**
 * Latest Vision validation run for a PO. 404 (not run yet) is an empty
 * state, not an error — same contract as v1's GET /api/orders/:id/validation
 * returning []. Vision auto-reruns a stale run on this GET, so we refetch
 * on mount the way v1 refetched every time the drawer opened.
 */
export function useOrderValidation(externalOrderNumber: string | undefined) {
  const client = useVisionClient()
  const tenantId = useTenantStore(s => s.tenantId)
  return useQuery<ValidationRun | null>({
    queryKey: ['order-validation', tenantId, externalOrderNumber],
    queryFn: async () => {
      try {
        return await client.getValidation(externalOrderNumber!)
      } catch (error) {
        if (error instanceof VisionApiError && error.status === 404) return null
        throw error
      }
    },
    enabled: Boolean(externalOrderNumber),
    staleTime: 30_000,
    refetchOnMount: 'always',
    retry: (failureCount, error) => {
      if (error instanceof VisionApiError && error.status === 404) return false
      return failureCount < 2
    },
  })
}

/**
 * Bulk-fetch catalog rows for the SKUs on an order and return a
 * `channel|channelItemCode → Item` lookup map. Matches the pulse-v1
 * pattern: one round-trip per PO detail view (not one per line), cached
 * for 10 minutes since Amazon catalog images rarely churn.
 */
export function useOrderItems(order: Order | undefined) {
  const client = useVisionClient()
  const tenantId = useTenantStore(s => s.tenantId)

  const channel = order?.channel
  const channelItemCodes = useMemo(() => {
    if (!order) return [] as string[]
    const seen = new Set<string>()
    for (const item of order.items) {
      if (item.channelSku) seen.add(item.channelSku)
    }
    return Array.from(seen).sort()
  }, [order])

  const query = useQuery({
    queryKey: ['order-items', tenantId, channel, channelItemCodes],
    queryFn: () => client.listItems({ channel: channel!, channelItemCodes }),
    enabled: Boolean(channel) && channelItemCodes.length > 0,
    staleTime: 600_000,
  })

  const map = useMemo(() => {
    const out = new Map<string, Item>()
    if (!query.data) return out
    for (const item of query.data.items) {
      out.set(`${item.channel}|${item.channelItemCode}`, item)
    }
    return out
  }, [query.data])

  return { map, isLoading: query.isLoading, error: query.error }
}

export function useAcknowledgeOrder(externalOrderNumber: string | undefined) {
  const client = useVisionClient()
  const qc = useQueryClient()
  const tenantId = useTenantStore(s => s.tenantId)
  const attempt = useRef<{ fingerprint: string; idempotencyKey: string } | null>(null)
  const mutation = useMutation({
    mutationFn: (body: {
      lines: { itemSequenceNumber: number; acknowledgedQuantity: number }[]
      expectedRejectedLines?: number[]
    }) => {
      const fingerprint = JSON.stringify(body)
      if (attempt.current?.fingerprint !== fingerprint) {
        attempt.current = { fingerprint, idempotencyKey: crypto.randomUUID() }
      }
      return client.createAcknowledgement(
        externalOrderNumber!,
        body,
        attempt.current.idempotencyKey,
      )
    },
    onSuccess: () => {
      attempt.current = null
      qc.invalidateQueries({ queryKey: ['order', tenantId, externalOrderNumber] })
      qc.invalidateQueries({ queryKey: ['order-validation', tenantId, externalOrderNumber] })
      qc.invalidateQueries({ queryKey: ['order-audit', tenantId, externalOrderNumber] })
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['orders-summary'] })
    },
  })
  return {
    ...mutation,
    reset: () => {
      attempt.current = null
      mutation.reset()
    },
  }
}

export function useCancelOrder(
  externalOrderNumber: string | undefined,
  orderId: string | undefined,
) {
  const client = useVisionClient()
  const qc = useQueryClient()
  const tenantId = useTenantStore(s => s.tenantId)
  const attempt = useRef<{ fingerprint: string; idempotencyKey: string } | null>(null)
  const mutation = useMutation({
    mutationFn: (body: OrderCancellationRequest) => {
      const fingerprint = JSON.stringify(body)
      if (attempt.current?.fingerprint !== fingerprint) {
        attempt.current = { fingerprint, idempotencyKey: crypto.randomUUID() }
      }
      return client.cancelOrder(externalOrderNumber!, body, attempt.current.idempotencyKey)
    },
    onSuccess: response => {
      attempt.current = null
      const updatedOrder = orderFromCancellationResponse(response)
      if (updatedOrder) {
        qc.setQueryData(['order', tenantId, externalOrderNumber], updatedOrder)
      }
      // A selected bulk-allocation snapshot contains pre-cancellation quantities.
      // Drop it instead of silently retaining an allocation that is now unsafe.
      if (orderId) useBulkSelectionStore.getState().remove(orderId)
      qc.invalidateQueries({ queryKey: ['order', tenantId, externalOrderNumber] })
      qc.invalidateQueries({ queryKey: ['order-audit', tenantId, externalOrderNumber] })
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['orders-summary'] })
      qc.invalidateQueries({ queryKey: ['fulfillable-orders'] })
    },
  })
  return {
    ...mutation,
    reset: () => {
      attempt.current = null
      mutation.reset()
    },
  }
}

export function useWarehousesQuery(enabled = true) {
  const client = useVisionClient()
  const tenantId = useTenantStore(s => s.tenantId)
  return useQuery({
    queryKey: ['warehouses', tenantId],
    queryFn: async () => {
      try {
        return await client.listWarehouses()
      } catch (error) {
        if (
          error instanceof VisionApiError &&
          (error.status === 403 || error.status === 404 || error.status === 501)
        ) {
          return { items: [] }
        }
        throw error
      }
    },
    enabled,
    staleTime: 10 * 60_000,
    retry: (failureCount, error) => {
      if (
        error instanceof VisionApiError &&
        (error.status === 403 || error.status === 404 || error.status === 501)
      ) {
        return false
      }
      return failureCount < 2
    },
  })
}

/**
 * Per-line ATP. 404 (Vision feed not ready) is an empty state, not an
 * error — the inventory column shows `—`.
 */
export function useOrderInventoryQuery(externalOrderNumber: string | undefined) {
  const client = useVisionClient()
  const tenantId = useTenantStore(s => s.tenantId)
  return useQuery<OrderInventory | null>({
    queryKey: ['order-inventory', tenantId, externalOrderNumber],
    queryFn: async () => {
      try {
        return await client.getOrderInventory(externalOrderNumber!)
      } catch (error) {
        if (
          error instanceof VisionApiError &&
          (error.status === 404 || error.status === 501 || error.status === 502)
        ) {
          return null
        }
        throw error
      }
    },
    enabled: Boolean(externalOrderNumber),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (
        error instanceof VisionApiError &&
        (error.status === 404 || error.status === 501 || error.status === 502)
      ) {
        return false
      }
      return failureCount < 2
    },
  })
}

/**
 * Live updates for the ATP grid, layered on top of {@link useOrderInventoryQuery}.
 *
 * Deliberately does not own the data. `useOrderInventoryQuery` fetches and owns the cache entry;
 * this only writes newer versions into it, so every consumer — the Inventory column, the
 * fulfill-from picker, the send gate — updates with no change on their side.
 *
 * Two failure modes are handled explicitly because neither is hypothetical:
 *
 * - Vision closes each stream at a lifetime cap (~15 min) so the browser reconnects with a fresh
 *   token, and every deploy severs every stream. Both land in `onReconnect`, where we invalidate
 *   rather than trust the stream: the fan-out behind it is at-most-once, so anything published
 *   during the gap is simply gone and only a refetch can recover it.
 * - A 401/403 is not retryable. The client stops on its own; we go quiet and leave the REST
 *   query's data on screen rather than blanking a grid the operator can still legitimately read.
 */
export function useOrderInventoryStream(externalOrderNumber: string | undefined) {
  const client = useVisionClient()
  const qc = useQueryClient()
  const tenantId = useTenantStore(s => s.tenantId)

  useEffect(() => {
    if (!externalOrderNumber) return
    const queryKey = ['order-inventory', tenantId, externalOrderNumber]

    const close = client.streamOrderInventory(externalOrderNumber, {
      onInventory: inventory => {
        qc.setQueryData<OrderInventory | null>(queryKey, inventory)
      },
      onReconnect: () => {
        void qc.invalidateQueries({ queryKey })
      },
      onFatal: error => {
        if (IS_DEV) console.warn('[pulse] inventory stream stopped', error)
      },
    })

    return close
  }, [client, qc, tenantId, externalOrderNumber])
}

/**
 * Whether each Sales Order under this PO is ready for lot assignment.
 *
 * **One request per PO, not per wave.** A single
 * `GET /v1/bc/sales-orders/from-po/lots/readiness?externalPoNumber={po}`
 * answers for every SO on the PO, so it is fetched once here at the detail
 * level and each Fulfilment cell picks its own row out of `results` by
 * `externalDocumentNo` (see `bcLotReadinessFor`). Firing it per row would be
 * one round-trip each for the same body.
 *
 * The staleness is deliberately short. What this reports is largely how far
 * through typing box quantities into Business Central somebody else has got,
 * which is exactly the sort of fact an operator refreshes to watch change.
 *
 * A missing feed is an empty state rather than an error, the same shape
 * `useOrderInventoryQuery` uses: Vision's lot proxy answers 404 until the
 * sibling routes are deployed, and a red panel on a PO whose fulfilment is
 * otherwise fine would be noise. A null response leaves every cell reading
 * `unknown`, which is the honest state — nobody looked — and is rendered as
 * such rather than as "the box quantities are missing".
 */
export function useBcLotReadinessQuery(externalOrderNumber: string | undefined, enabled = true) {
  const tenantId = useTenantStore(s => s.tenantId)
  return useQuery<BcLotReadinessResponse | null>({
    queryKey: ['bc-lot-readiness', tenantId, externalOrderNumber],
    queryFn: async () => {
      try {
        return await getBcLotReadiness(externalOrderNumber!, { getToken: getVisionAccessToken })
      } catch (error) {
        if (lotReadinessUnavailable(error)) return null
        throw error
      }
    },
    enabled: enabled && Boolean(externalOrderNumber),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (lotReadinessUnavailable(error)) return false
      return failureCount < 2
    },
  })
}

/** The route is not deployed, or Vision could not reach Heimdall. Not an error to show. */
function lotReadinessUnavailable(error: unknown): boolean {
  return (
    error instanceof HeimdallApiError &&
    (error.status === 404 || error.status === 501 || error.status === 502)
  )
}

export function useWarehouseRelease(externalOrderNumber: string | undefined) {
  const client = useVisionClient()
  const qc = useQueryClient()
  const tenantId = useTenantStore(s => s.tenantId)
  return useMutation({
    mutationFn: (body: { lines: WarehouseReleaseLine[] }) =>
      client.createWarehouseRelease(externalOrderNumber!, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', tenantId, externalOrderNumber] })
      qc.invalidateQueries({ queryKey: ['order-audit', tenantId, externalOrderNumber] })
      qc.invalidateQueries({ queryKey: ['order-inventory', tenantId, externalOrderNumber] })
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['orders-summary'] })
    },
  })
}

/** Flipkart CSV upload — Pulse Entra → Vision → Valkyrie. */
export function useUploadChannelPos() {
  const qc = useQueryClient()
  const tenantId = useTenantStore(s => s.tenantId)
  const client = useMemo(
    () =>
      createValkyrieUploadClient({
        getToken: getVisionAccessToken,
        getTenant: () => tenantId,
      }),
    [tenantId],
  )
  return useMutation({
    mutationFn: (body: { channel: string; files: File[] }) =>
      client.uploadChannelPos(body.channel, body.files),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] })
      qc.invalidateQueries({ queryKey: ['orders-summary'] })
    },
  })
}

/**
 * Admin-only force re-run of Vision ITEM / CASEPACK / PRICE / HSN.
 * Writes the fresh run into the validation cache so line chips update
 * without a full page reload. Does not touch ack state — Vision treats
 * this as display-only even on ACKNOWLEDGED POs.
 */
export function useRerunValidation(externalOrderNumber: string | undefined) {
  const client = useVisionClient()
  const qc = useQueryClient()
  const tenantId = useTenantStore(s => s.tenantId)
  return useMutation({
    mutationFn: () => client.rerunValidation(externalOrderNumber!),
    onSuccess: run => {
      qc.setQueryData(['order-validation', tenantId, externalOrderNumber], run)
    },
  })
}
